import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Copy, Check, Upload, CheckCircle2, Loader2, QrCode } from "lucide-react";
import api from "@/lib/api";

const INDIAN_STATES = ["Andhra Pradesh", "Telangana", "Tamil Nadu", "Karnataka", "Kerala", "Maharashtra", "Delhi", "Uttar Pradesh", "Gujarat", "Rajasthan", "West Bengal", "Other"];

export const BookingModal = ({ service, open, onClose, initialDate }) => {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [booking, setBooking] = useState(null);
  const [upi, setUpi] = useState(null);
  const [copied, setCopied] = useState(false);

  const [form, setForm] = useState({
    booking_date: "", time_slot: "", num_pilgrims: 1, amount: 0,
    lead_name: "", id_proof: "", mobile: "", email: "", state: "",
  });
  const [screenshot, setScreenshot] = useState(null);
  const [txnId, setTxnId] = useState("");
  const [avail, setAvail] = useState(null);
  const [roomType, setRoomType] = useState(null);

  useEffect(() => {
    const d = form.booking_date;
    if (!service || !d) { setAvail(null); return; }
    let active = true;
    api.get("/availability", { params: { service_id: service.id, date: d } })
      .then(({ data }) => { if (active) setAvail(data); })
      .catch(() => { if (active) setAvail(null); });
    return () => { active = false; };
  }, [form.booking_date, service]);

  useEffect(() => {
    if (service) {
      setStep(1);
      setBooking(null);
      setScreenshot(null);
      setTxnId("");
      setRoomType(service.roomTypes ? service.roomTypes[0] : null);
      setForm({
        booking_date: initialDate || "", time_slot: service.slots ? "" : "NA", num_pilgrims: 1,
        amount: service.amount || 0, lead_name: "", id_proof: "", mobile: "", email: "", state: "",
      });
    }
  }, [service, initialDate]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const perUnit = service?.roomTypes ? (roomType?.amount || 0) : (service?.amount || 0);
  const totalAmount = service?.customAmount ? Number(form.amount || 0) : perUnit * Number(form.num_pilgrims || 1);

  const loadUpi = async (amount) => {
    try {
      const { data } = await api.get(`/settings/upi`, { params: { amount: amount || undefined } });
      setUpi(data);
    } catch { toast.error("Could not load payment details"); }
  };

  const submitBooking = async () => {
    if (!form.booking_date || !form.lead_name || !form.mobile) {
      toast.error("Please fill date, name and mobile number");
      return;
    }
    if (service.customAmount && (!form.amount || Number(form.amount) <= 0)) {
      toast.error("Please enter a donation amount");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        service_id: service.id,
        service_name: service.name,
        booking_date: form.booking_date,
        time_slot: form.time_slot || null,
        room_type: roomType?.name || null,
        num_pilgrims: Number(form.num_pilgrims),
        amount: totalAmount,
        lead_name: form.lead_name,
        id_proof: form.id_proof,
        mobile: form.mobile,
        email: form.email,
        state: form.state,
        pilgrims: [],
      };
      const { data } = await api.post("/bookings", payload);
      setBooking(data);
      await loadUpi(totalAmount);
      setStep(2);
    } catch (e) { toast.error(e.response?.data?.detail || "Booking failed, please retry"); }
    finally { setSubmitting(false); }
  };

  const joinWaitlist = async () => {
    if (!form.lead_name || !form.mobile) { toast.error("Enter your name and mobile to join the waitlist"); return; }
    setSubmitting(true);
    try {
      await api.post("/waitlist", {
        service_id: service.id, service_name: service.name, date: form.booking_date,
        name: form.lead_name, mobile: form.mobile, email: form.email,
      });
      toast.success("You're on the waitlist! We'll email you if a seat opens up.");
      onClose();
    } catch { toast.error("Could not join waitlist"); }
    finally { setSubmitting(false); }
  };

  const copyUpi = () => {    navigator.clipboard.writeText(upi.upi_id);
    setCopied(true);
    toast.success("UPI ID copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Image too large (max 4MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setScreenshot(reader.result);
    reader.readAsDataURL(file);
  };

  const submitPayment = async () => {
    if (!txnId || !screenshot) {
      toast.error("Enter transaction ID and upload payment screenshot");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/bookings/${booking.reference}/payment`, { transaction_id: txnId, screenshot });
      toast.success("Payment submitted! Pending admin approval");
      setStep(3);
    } catch { toast.error("Could not submit payment"); }
    finally { setSubmitting(false); }
  };

  if (!service) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="booking-modal">
        <DialogHeader>
          <DialogTitle className="font-cinzel text-xl text-amber-950">{service.name}</DialogTitle>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-2">
          {["Details", "Payment", "Done"].map((s, i) => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-1.5 text-xs font-semibold ${step >= i + 1 ? "text-[#E65100]" : "text-slate-400"}`}>
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-white ${step >= i + 1 ? "bg-[#E65100]" : "bg-slate-300"}`}>{i + 1}</span>
                {s}
              </div>
              {i < 2 && <div className={`flex-1 h-0.5 ${step > i + 1 ? "bg-[#E65100]" : "bg-slate-200"}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* STEP 1: Details */}
        {step === 1 && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Booking Date *</Label>
                <Input type="date" value={form.booking_date} onChange={(e) => upd("booking_date", e.target.value)} data-testid="booking-date-input" />
                {avail && avail.remaining !== null && (
                  <div className={`mt-1 text-xs font-semibold ${avail.remaining > 0 ? "text-emerald-700" : "text-red-600"}`} data-testid="availability-badge">
                    {avail.remaining > 0 ? `${avail.remaining} of ${avail.limit} seats left` : "Sold out for this date"}
                  </div>
                )}
              </div>
              {service.slots && (
                <div>
                  <Label>Time Slot</Label>
                  <Select value={form.time_slot} onValueChange={(v) => upd("time_slot", v)}>
                    <SelectTrigger data-testid="booking-slot-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {service.slots.map((sl) => <SelectItem key={sl} value={sl}>{sl}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {service.customAmount ? (
              <div>
                <Label>Donation Amount (₹) *</Label>
                <Input type="number" min="1" value={form.amount} onChange={(e) => upd("amount", e.target.value)} placeholder="Enter amount" data-testid="booking-amount-input" />
              </div>
            ) : service.roomTypes ? (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Room Type</Label>
                  <Select value={roomType?.name} onValueChange={(v) => setRoomType(service.roomTypes.find((r) => r.name === v))}>
                    <SelectTrigger data-testid="booking-roomtype-select"><SelectValue placeholder="Select room" /></SelectTrigger>
                    <SelectContent>
                      {service.roomTypes.map((r) => <SelectItem key={r.name} value={r.name}>{r.name} — ₹{r.amount}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Number of Rooms</Label>
                  <Input type="number" min="1" max="10" value={form.num_pilgrims} onChange={(e) => upd("num_pilgrims", e.target.value)} data-testid="booking-rooms-input" />
                </div>
              </div>
            ) : (
              <div>
                <Label>Number of Pilgrims</Label>
                <Input type="number" min="1" max="10" value={form.num_pilgrims} onChange={(e) => upd("num_pilgrims", e.target.value)} data-testid="booking-pilgrims-input" />
              </div>
            )}

            {service.location && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900" data-testid="accommodation-info">
                <div><b>Check-in:</b> {service.checkIn} · <b>Check-out:</b> {service.checkOut} · <b>Min {service.minPersons} persons/room</b></div>
                <div className="mt-1"><b>Location:</b> {service.location}</div>
              </div>
            )}

            <div>
              <Label>Lead Pilgrim Name *</Label>
              <Input value={form.lead_name} onChange={(e) => upd("lead_name", e.target.value)} placeholder="Full name" data-testid="booking-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Mobile *</Label>
                <Input value={form.mobile} onChange={(e) => upd("mobile", e.target.value)} placeholder="10-digit mobile" data-testid="booking-mobile-input" />
              </div>
              <div>
                <Label>ID Proof (Aadhaar)</Label>
                <Input value={form.id_proof} onChange={(e) => upd("id_proof", e.target.value)} placeholder="Optional" data-testid="booking-id-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email</Label>
                <Input value={form.email} onChange={(e) => upd("email", e.target.value)} placeholder="Optional" data-testid="booking-email-input" />
              </div>
              <div>
                <Label>State</Label>
                <Select value={form.state} onValueChange={(v) => upd("state", v)}>
                  <SelectTrigger data-testid="booking-state-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {INDIAN_STATES.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mt-2">
              <span className="text-sm font-semibold text-amber-900">Total Payable</span>
              <span className="font-cinzel text-xl font-bold text-[#E65100]" data-testid="booking-total">₹{totalAmount.toLocaleString("en-IN")}</span>
            </div>

            {avail && avail.remaining === 0 ? (
              <div className="space-y-2">
                <div className="text-center text-sm text-red-600 font-semibold">This date is sold out.</div>
                <Button onClick={joinWaitlist} disabled={submitting} className="w-full bg-[#7A0016] hover:bg-[#50000E] text-white font-semibold py-6" data-testid="join-waitlist-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Join Waitlist — notify me if a seat opens"}
                </Button>
              </div>
            ) : (
              <Button onClick={submitBooking} disabled={submitting} className="w-full bg-[#E65100] hover:bg-[#BF360C] text-white font-semibold py-6" data-testid="booking-submit-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Proceed to Payment →"}
              </Button>
            )}
          </div>
        )}

        {/* STEP 2: Payment */}
        {step === 2 && upi && (
          <div className="space-y-4">
            <div className="text-center bg-amber-50 border border-amber-200 rounded-lg py-2">
              <span className="text-xs text-slate-600">Booking Ref: </span>
              <span className="font-bold text-[#7A0016]" data-testid="booking-reference">{booking.reference}</span>
            </div>

            <div className="flex flex-col items-center">
              <div className="text-xs uppercase tracking-widest text-[#E65100] font-bold mb-2 flex items-center gap-1"><QrCode className="w-4 h-4" /> Scan & Pay ₹{totalAmount.toLocaleString("en-IN")}</div>
              <div className="p-3 bg-white border-2 border-[#DAA520] rounded-xl shadow-md">
                <img src={upi.qr} alt="UPI QR" className="w-48 h-48" data-testid="payment-qr-code" />
              </div>
            </div>

            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <div className="text-xs text-emerald-700 font-semibold uppercase tracking-wide mb-1">Pay to UPI ID</div>
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono font-bold text-emerald-900 text-lg" data-testid="upi-id-display">{upi.upi_id}</span>
                <Button size="sm" onClick={copyUpi} className={`${copied ? "bg-green-600" : "bg-emerald-600 hover:bg-emerald-700"} text-white`} data-testid="upi-id-copy-btn">
                  {copied ? <><Check className="w-4 h-4 mr-1" /> Copied</> : <><Copy className="w-4 h-4 mr-1" /> Copy</>}
                </Button>
              </div>
            </div>

            <div className="sacred-divider" />
            <div className="text-sm font-semibold text-amber-950">After paying, submit your payment proof:</div>

            <div>
              <Label>UPI Transaction ID / UTR *</Label>
              <Input value={txnId} onChange={(e) => setTxnId(e.target.value)} placeholder="e.g. 4567XXXX1234" data-testid="payment-txn-input" />
            </div>

            <div>
              <Label>Payment Screenshot *</Label>
              <label className="mt-1 flex flex-col items-center justify-center border-2 border-dashed border-amber-300 rounded-lg py-4 cursor-pointer hover:bg-amber-50 transition-colors">
                {screenshot ? (
                  <img src={screenshot} alt="preview" className="h-28 rounded object-contain" data-testid="payment-screenshot-preview" />
                ) : (
                  <><Upload className="w-6 h-6 text-amber-500 mb-1" /><span className="text-xs text-slate-500">Tap to upload screenshot</span></>
                )}
                <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="payment-screenshot-input" />
              </label>
            </div>

            <Button onClick={submitPayment} disabled={submitting} className="w-full bg-[#E65100] hover:bg-[#BF360C] text-white font-semibold py-6" data-testid="payment-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Payment Proof"}
            </Button>
          </div>
        )}

        {/* STEP 3: Done */}
        {step === 3 && (
          <div className="text-center py-6 space-y-3" data-testid="booking-success">
            <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto" />
            <h3 className="font-cinzel text-xl font-bold text-amber-950">Booking Request Submitted!</h3>
            <p className="text-sm text-slate-600">
              Your booking <span className="font-bold text-[#7A0016]">{booking.reference}</span> is <b>pending admin approval</b>.
              Track it anytime under <b>Booking Status</b> using your reference or mobile number.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-left text-sm">
              <div className="flex justify-between py-1"><span className="text-slate-500">Service</span><span className="font-semibold">{service.short}</span></div>
              <div className="flex justify-between py-1"><span className="text-slate-500">Date</span><span className="font-semibold">{form.booking_date}</span></div>
              <div className="flex justify-between py-1"><span className="text-slate-500">Amount Paid</span><span className="font-semibold text-[#E65100]">₹{totalAmount.toLocaleString("en-IN")}</span></div>
            </div>
            <Button onClick={onClose} className="w-full bg-[#7A0016] hover:bg-[#50000E] text-white" data-testid="booking-done-btn">Close</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
